import { useCallback } from 'react'
import { type OfferRecommendationFragment } from '@/services/graphql/generated'
import { logger } from '@/services/logger/logger.client'
import { useTracking } from '@/services/Tracking/useTracking'
import { useAddToCart } from '@/utils/useAddToCart'
import { datadogRum } from '@datadog/browser-rum'

export function useAddRecommendationOfferToCart({
  shopSessionId,
  onAddSuccess,
}: {
  shopSessionId: string
  onAddSuccess?: () => void
}) {
  const tracking = useTracking()

  const [addToCart, loading] = useAddToCart({
    shopSessionId,
    onSuccess(offerId, cart) {
      const addedOffer = cart.items.find((item) => item.productOffer.id === offerId)!.productOffer
      const productName = addedOffer.product.name

      logger.info('Quick Add | Added offer to cart', {
        productOfferId: addedOffer.id,
        product: productName,
      })

      if (onAddSuccess) {
        onAddSuccess()
      }
    },
  })

  const addOfferToCart = useCallback(
    async (offer: OfferRecommendationFragment) => {
      const productName = offer.product.name

      datadogRum.addAction('Quick Add To Cart', {
        type: 'complete',
        productOfferId: offer.id,
        product: productName,
      })
      tracking.reportAddToCart(offer, 'recommendations')
      await addToCart(offer.id)
    },
    [addToCart, tracking],
  )

  return [addOfferToCart, loading] as const
}
